import { debounce } from 'throttle-debounce';

import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import {
  fetchAllElements,
  fetchElement,
  fetchElementById
} from '@client/core/utils/dom/domUtils.js';
import {
  Brand,
  DisplayPlacementId,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

const FILTER_LIST_ID = 'advertising--filters-bottom-marker';
const MARGIN = 100;
const DEBOUNCE_MS = 200;
const HEIGHT_CHANGE_THRESHOLD = 10;
const ELEMENT_CHECK_INTERVAL_MS = 100;
const MAX_ELEMENT_CHECK_ATTEMPTS = 100;

const fetchFilterListElement = () => fetchElementById(FILTER_LIST_ID);

function isOverlapping(
  adElement: Element,
  filterListElement: Element
): boolean {
  const filterBottomPlacement =
    filterListElement.getBoundingClientRect().bottom + window.scrollY + MARGIN;

  const elementTop = adElement.getBoundingClientRect().top + window.scrollY;

  return elementTop < filterBottomPlacement;
}

export function isBannerOverlappingFilters(placementId: PlacementId) {
  const config = $config.get();
  if (config?.brand !== Brand.Dba) return false;

  const filterListElement = fetchFilterListElement();
  if (!filterListElement) return false;

  const adElement = fetchElement(
    `advt-component[placementid="${placementId}"]`
  );
  if (!adElement) return false;

  return isOverlapping(adElement, filterListElement);
}

export function checkBannerCollisions() {
  const config = $config.get();
  if (config?.brand !== Brand.Dba) return false;

  const filterListElement = fetchFilterListElement();
  if (!filterListElement) return false;

  // Check for advt-loaded to make sure the ad is done with its cycle, and this way we won't check against collapsed banners
  const middleElements = fetchAllElements(
    'advt-component[placementid^="advt_middle_"].intermingles-ad.advt-loaded'
  );
  const bottomElements = fetchAllElements(
    `advt-component[placementid="${DisplayPlacementId.AdvtBottom1}"].advt-loaded`
  );

  const allCollapsibleElements = [...middleElements, ...bottomElements];

  if (allCollapsibleElements.length === 0) return false;

  const hasOverlap = Array.from(allCollapsibleElements).some((adElement) => {
    if (!isOverlapping(adElement, filterListElement)) return false;

    // a bit hacky, but hey we'r advertising team
    const placementId = adElement.getAttribute(
      'placementid'
    ) as PlacementId | null;

    if (!placementId) return false;

    updateMetricByKey(placementId, PlacementStatus.CollapsedOnCollision);
    updatePlacementKeyValueById(placementId, 'status', 'collapsedOnCollision');

    return true;
  });

  return hasOverlap;
}

let filterObserverInstance: ResizeObserver | null = null;
let elementCheckIntervalId: ReturnType<typeof setInterval> | null = null;
let debouncedCheckFunction: ReturnType<typeof debounce> | null = null;
let lastKnownFilterHeight = 0;
let isSettingUp = false;

function setupFilterResizeObserver(filterContainer: Element) {
  if (filterObserverInstance) {
    filterObserverInstance.disconnect();
    filterObserverInstance = null;
  }

  if (debouncedCheckFunction) {
    debouncedCheckFunction.cancel();
    debouncedCheckFunction = null;
  }

  lastKnownFilterHeight = filterContainer.getBoundingClientRect().height;

  debouncedCheckFunction = debounce(DEBOUNCE_MS, () => {
    const currentHeight = filterContainer.getBoundingClientRect().height;

    if (
      Math.abs(currentHeight - lastKnownFilterHeight) > HEIGHT_CHANGE_THRESHOLD
    ) {
      lastKnownFilterHeight = currentHeight;
      checkBannerCollisions();
    }
  });

  filterObserverInstance = new ResizeObserver(debouncedCheckFunction);
  filterObserverInstance.observe(filterContainer);
  checkBannerCollisions();
}

/**
 * Observes the filter container for size changes and checks for ad overlap.
 * This handles cases where filters expand dynamically (e.g., "See all" clicks)
 * that may not trigger the EXPANDABLE_UPDATE event.
 *
 * If the filter marker element is not yet in the DOM, this function will poll
 * for it to appear using setInterval (more efficient than MutationObserver on document.body).
 */
export function observeFilterContainerChanges() {
  if (isSettingUp || filterObserverInstance || elementCheckIntervalId) {
    return;
  }

  if (typeof ResizeObserver === 'undefined') {
    return;
  }

  isSettingUp = true;

  try {
    const filterListElement = fetchFilterListElement();
    if (!filterListElement) {
      let attempts = 0;

      elementCheckIntervalId = setInterval(() => {
        try {
          attempts += 1;

          const element = fetchFilterListElement();
          if (element?.parentElement) {
            if (elementCheckIntervalId) {
              clearInterval(elementCheckIntervalId);
              elementCheckIntervalId = null;
            }
            setupFilterResizeObserver(element.parentElement);
            isSettingUp = false;
          } else if (attempts >= MAX_ELEMENT_CHECK_ATTEMPTS) {
            if (elementCheckIntervalId) {
              clearInterval(elementCheckIntervalId);
              elementCheckIntervalId = null;
            }
            isSettingUp = false;
          }
        } catch {
          if (elementCheckIntervalId) {
            clearInterval(elementCheckIntervalId);
            elementCheckIntervalId = null;
          }
          isSettingUp = false;
        }
      }, ELEMENT_CHECK_INTERVAL_MS);

      return;
    }

    const filterContainer = filterListElement.parentElement;
    if (!filterContainer) {
      isSettingUp = false;
      return;
    }

    setupFilterResizeObserver(filterContainer);
    isSettingUp = false;
  } catch {
    isSettingUp = false;
    if (elementCheckIntervalId) {
      clearInterval(elementCheckIntervalId);
      elementCheckIntervalId = null;
    }
  }
}

export function disconnectFilterObserver() {
  if (filterObserverInstance) {
    filterObserverInstance.disconnect();
    filterObserverInstance = null;
  }

  if (debouncedCheckFunction) {
    debouncedCheckFunction.cancel();
    debouncedCheckFunction = null;
  }

  if (elementCheckIntervalId) {
    clearInterval(elementCheckIntervalId);
    elementCheckIntervalId = null;
  }

  isSettingUp = false;
  lastKnownFilterHeight = 0;
}

let cleanupHandlersRegistered = false;

function registerCleanupHandlers() {
  if (cleanupHandlersRegistered) return;
  cleanupHandlersRegistered = true;

  window.addEventListener('pagehide', disconnectFilterObserver, {
    once: true
  });

  window.addEventListener('beforeunload', disconnectFilterObserver, {
    once: true
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      disconnectFilterObserver();
    }
  });
}

export function initializeFilterCollisionDetection() {
  const config = $config.get();
  if (config?.brand !== Brand.Dba) return;

  registerCleanupHandlers();
  observeFilterContainerChanges();
}
